import React, { useContext, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { WelletContext } from '../../../context/WelletContext';
import { format } from 'date-fns';
import { getDateFromOffset } from '../../../helpers/dates';
import { useTranslation } from 'react-i18next';
import { SkeletonEarningsNotPaid } from '../../../components/Skeletons/SkeletonMyMoney';
import Balance from '../../../components/Balance/Balance';
import ContentApp from '../../../components/ContentApp';
import EarningsNotPaid from '../../../components/Payments/EarningsNotPaid';
import AccountHeader from '../../../components/AccountHeader/AccountHeader';
import NavSelector from '../../../components/NavSelector/NavSelector';
import PaymentsList from './Payout';

const Payments = ({ history }) => {
  const { t } = useTranslation();
  const welletContext = useContext(WelletContext);
  const user = useSelector((state) => state.associates.user);
  const today = format(new Date(getDateFromOffset(user.companyUtcMinutesOffset)), 'yyyy-MM-dd');
  const [earningsNotPaid, setEarningsNotPaid] = useState(null);
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [selectedDate, setSelectedDate] = useState(today);
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [calendarVisible, setCalendarVisible] = useState(false);
  const currency = useSelector((state) => state.currency.default.shortForm);

  const getEarningsNotPaid = async () => {
    setLoading(true);
    try {
      const earningsNotPaid = await welletContext.apis.tickets.get(
        `/associates/GetEarningsNotPaid`,
      );
      setEarningsNotPaid(earningsNotPaid.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const getBalanceSummary = async (date = selectedDate, includeDetails = true) => {
    setLoadingSummary(true);
    try {
      const balance = await welletContext.apis.tickets.get(
        `/associates/GetEarnings?from=${summary ? date : today}&to=${
          summary ? date : today
        }&includeDetails=${includeDetails}`,
      );
      setSummary(balance.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingSummary(false);
    }
  };

  const handleChangeDate = (day) => {
    if (format(day, 'yyyy-MM-dd') === selectedDate) {
      setSelectedDate(selectedDate);
    } else {
      setSelectedDate(day);
    }
    setCalendarVisible(!calendarVisible);
    getBalanceSummary(format(day, 'yyyy-MM-dd'), true);
  };

  useEffect(() => {
    getEarningsNotPaid();
    getBalanceSummary(selectedDate, true);
  }, []);

  return (
    <>
      <AccountHeader title={t('payments')} icon='config' pathName='/configuration' />
      <ContentApp header menu classes='position-relative'>
        <div className='my-money p-0'>
          <Balance
            title={t('earnings')}
            fontSize='1.8rem'
            showText={false}
            loading={loading}
            history={history}
            mpVerified={loading}
            summary={summary}
            handleChangeDate={handleChangeDate}
            loadingSummary={loadingSummary}
            selectedDate={selectedDate}
            setCalendarVisible={setCalendarVisible}
            calendarVisible={calendarVisible}
            todayBalance={summary?.amount}
          />
          {!loading ? (
            <div className='earnings-not-paid'>
              <EarningsNotPaid amount={earningsNotPaid?.amount} currency={currency} />
            </div>
          ) : (
            <div className='mt-2'>{SkeletonEarningsNotPaid()}</div>
          )}
          <div>
            <div className='bold mt-4 mb-4'>{t('recentPayments')}</div>
            <PaymentsList history={history} />
          </div>
        </div>
        <NavSelector history={history} />
      </ContentApp>
    </>
  );
};

export default Payments;
